import { useState } from 'react';
import { Actions, Async, F1 } from '@client/new/helpers/types';
import {
  Author,
  useGetDirectMessageStatusQuery,
  useGetUserInfoQuery,
  useSetDirectMessagingMutation,
} from 'generated/graphql';
import toast from 'react-hot-toast';
import { BusinessModel } from '@playbook-technologies/common-enums';
import { getDmEnabled } from '@client/new/common/features.helpers';

export type InboxModelParams = {
  influencerId: number;
  userId: string;
};

export type InboxModel = {
  dispatch: InboxViewDispatcher;
  submitting: boolean;
  influencerId: number;
  userId: string;
  canDirectMessage: boolean;
  offering: Array<string>;
  premiumFeaturesOTPIds: Array<number | null>;
  isBundleBusinessModel: boolean;
  userInfo: Author | undefined;
};

export type InboxActionsMap = {
  submitEnable: null;
  showUserInfo: number;
  clearUserInfo: null;
};

export type InboxActions = Actions<InboxActionsMap>;

export type InboxViewDispatcher = F1<InboxActions>;

export const useInboxModel = ({
  userId,
  influencerId,
}: InboxModelParams): Async<InboxModel> => {
  const [submitting, setSubmitting] = useState(false);
  const [setDirectMessagingMutation] = useSetDirectMessagingMutation();
  const [infoUserId, setUserInfoId] = useState<number | null>(null);

  const { data, loading, error, refetch } = useGetDirectMessageStatusQuery({
    fetchPolicy: 'network-only',
    variables: { id: influencerId },
    skip: !influencerId,
  });

  const { data: userInfo, loading: loadingUserInfo } = useGetUserInfoQuery({
    variables: {
      userIds: [infoUserId!],
    },
    skip: !infoUserId,
  });

  const canDirectMessage = getDmEnabled(data?.creator?.features);

  if (loading) {
    return {
      type: 'fetching',
    };
  }

  if (error) {
    return {
      type: 'fetch-error',
      value: 'Could not fetch data',
    };
  }

  const onEnableDirectMessaging = async () => {
    setSubmitting(true);
    try {
      await setDirectMessagingMutation({
        variables: {
          data: {
            influencerId: influencerId,
            isEnabled: true,
          },
        },
      });
      await refetch();
      toast.success('Direct Messaging is now available!');
    } catch (e) {
      toast.error('Something went wrong!');
    } finally {
      setSubmitting(false);
    }
  };

  const dispatch: InboxViewDispatcher = (action) => {
    switch (action.type) {
      case 'submitEnable':
        onEnableDirectMessaging();
        return null;
      case 'showUserInfo':
        return setUserInfoId(action.payload);
      case 'clearUserInfo':
        return setUserInfoId(null);
    }
  };

  return {
    type: 'fetched',
    value: {
      dispatch,
      submitting,
      influencerId,
      userId,
      canDirectMessage,
      premiumFeaturesOTPIds:
        data?.creator?.customizations?.premiumFeaturesOTPIds || [],
      offering: data?.creator?.offering || [],
      isBundleBusinessModel:
        data?.creator?.businessModel === BusinessModel.Bundle,
      userInfo: loadingUserInfo ? undefined : userInfo?.getUsersInfoByIds?.[0],
    },
  };
};
